import React, { useCallback, useEffect, useState } from 'react';
import Loader from '../../component/common/Loader';
import SearchInput from '../../component/common/Input/SearchInput';
import Table from '../../component/common/Table';
import Pagination from '../../component/common/table2/Pagination';
import { fetchHabitsUsers } from './habitsHubApi';
import { TableCard } from '../../component/common/designSystem';

const PER_PAGE = 10;

/**
 * Shared, searchable users table for the Habit Tracker section.
 * B2C Logs renders it unscoped (accountType="B2C"); the company detail page
 * scopes it via companyId. `onOpenUser(rowData)` decides where a row
 * navigates so each flow keeps its own detail URL. `showCompanyColumn`
 * hides the redundant column when the whole table is one company.
 */
const HabitUsersTable = ({
  accountType = '',
  companyId = '',
  onOpenUser,
  tableName,
  showCompanyColumn = !companyId,
  // The company panel passes its own company/v1 fetcher (same contract).
  fetcher = fetchHabitsUsers
}) => {
  const [loader, setLoader] = useState(false);
  const [rows, setRows] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');

  const fetchData = useCallback(
    async (page, search) => {
      setLoader(true);
      try {
        const response = await fetcher({
          page,
          perPage: PER_PAGE,
          search,
          accountType,
          companyId
        });
        // Company mode includes employees who never tracked — show dashes
        // instead of zeroed stats for them.
        const mapped = (response.data || []).map((row) => ({
          ...row,
          bestStreakText: row.totalCompletions > 0 ? `${row.bestStreak} days` : '-',
          lastCompletedAt: row.lastCompletedAt || '-'
        }));
        setRows(mapped);
        setTotalCount(response.totalCount || 0);
      } catch (error) {
        console.error('HabitUsersTable fetchData error:', error);
        setRows([]);
        setTotalCount(0);
      } finally {
        setLoader(false);
      }
    },
    [accountType, companyId, fetcher]
  );

  // Debounced fetch on search / page / scope change.
  useEffect(() => {
    const timeout = setTimeout(() => {
      fetchData(currentPage, searchTerm);
    }, 200);
    return () => clearTimeout(timeout);
  }, [fetchData, searchTerm, currentPage]);

  const columns = [
    { title: 'Name', key: 'user_profile', sortable: false, align: 'left', type: 'profile' },
    // The company column only makes sense when the table is not already
    // scoped to a single company.
    ...(showCompanyColumn
      ? [{ title: 'Company', key: 'company', sortable: false, align: 'center', type: 'text3' }]
      : []),
    { title: 'Active Habits', key: 'activeHabits', sortable: false, align: 'center', type: 'number' },
    { title: 'Completions', key: 'totalCompletions', sortable: false, align: 'center', type: 'number' },
    { title: 'Best Streak', key: 'bestStreakText', sortable: false, align: 'center', type: 'text3' },
    { title: 'Last Completed', key: 'lastCompletedAt', sortable: false, align: 'center', type: 'text3' },
    { title: '', key: 'action', align: 'right', type: 'action', arrowClickFun: onOpenUser }
  ];

  return (
    <div>
      {loader ? <Loader /> : null}

      <div className="flex justify-end">
        <SearchInput
          id={`${tableName}-search`}
          name={`${tableName}-search`}
          type="text"
          value={searchTerm}
          onChange={(e) => {
            setCurrentPage(1);
            setSearchTerm(e.target.value);
          }}
          placeholder="Search users"
          className="w-full sm:w-auto px-3 py-2"
        />
      </div>

      <TableCard
          pagination={{
            currentPage: currentPage,
            totalCount: totalCount,
            pageSize: PER_PAGE,
            onPageChange: (page) => setCurrentPage(page)
          }}
        >
          <Table
          columns={columns}
          data={rows}
          name={tableName}
          bottomBorder={totalCount > PER_PAGE}
          loader={loader}
        />
        </TableCard>

      
    </div>
  );
};

export default HabitUsersTable;
